import { EditToolBackgroundColorPicker } from '@carboncopyco/components';
import {
  ColorOutlineIcon,
  DropShadowIcon,
  PhotoSquareOutlineIcon,
  RestartIcon,
  VOutlineIcon,
} from '@carboncopyco/icons';
import type { BackgroundType, EditToolColorValues } from '@carboncopyco/types';
import {
  DropdownMenuItem,
  ExpandDownIcon,
  Separator,
  TextBase,
} from '@carboncopyco/ui';
import { useState } from 'react';
import { ImagePicker } from './ImagePicker';

type EditToolCoverPickerTypes = {
  color: EditToolColorValues;
  onColorChange: (values: EditToolColorValues) => void;
  image: string;
  onImageChange: (image: string) => void;
  isImageBlurred: boolean;
  setIsImageBlurred: (isBlurred: boolean) => void;
  hasCoverChanges: boolean;
  onResetCoverChanges: () => void;
  backgroundType: BackgroundType;
  onBackgroundTypeChange: (type: BackgroundType) => void;
};

export const CoverPicker = ({
  color,
  onColorChange,
  image,
  onImageChange,
  isImageBlurred,
  setIsImageBlurred,
  hasCoverChanges,
  onResetCoverChanges,
  backgroundType,
  onBackgroundTypeChange,
}: EditToolCoverPickerTypes) => {
  const [isSelectWindowOpen, setIsSelectWindowOpen] = useState(false);

  return (
    <div className="flex flex-col py-2.5 ">
      <div className="flex flex-row">
        <DropShadowIcon size={17} />
        <div
          className="flex flex-row cursor-pointer justify-center items-center pl-2"
          onClick={() => setIsSelectWindowOpen(!isSelectWindowOpen)}
        >
          <TextBase
            label="Background color"
            color="text-ds-natural-600"
          />
          <div className="pl-2">
            <ExpandDownIcon
              rotate={isSelectWindowOpen}
              size={17}
            />
          </div>
        </div>
      </div>
      {isSelectWindowOpen && (
        <div className="absolute mt-6 rounded-lg shadow-ds-md w-48 bg-white z-10">
          <DropdownMenuItem
            label="Color"
            iconLeft={<ColorOutlineIcon size={24} />}
            onClick={() => {
              onBackgroundTypeChange('color');
              setIsSelectWindowOpen(false);
            }}
            iconRight={
              backgroundType === 'color' ? <VOutlineIcon size={24} /> : null
            }
          />
          <DropdownMenuItem
            label="Image"
            iconLeft={<PhotoSquareOutlineIcon size={24} />}
            onClick={() => {
              onBackgroundTypeChange('image');
              setIsSelectWindowOpen(false);
            }}
            iconRight={
              backgroundType === 'image' ? <VOutlineIcon size={24} /> : null
            }
          />
          <Separator horizontal />
          <DropdownMenuItem
            label="Reset to default"
            iconLeft={<RestartIcon size={24} />}
            onClick={() => {
              onResetCoverChanges();
              setIsSelectWindowOpen(false);
            }}
            disabled={!hasCoverChanges}
          />
        </div>
      )}
      <div className="pl-6">
        {backgroundType === 'color' ? (
          <EditToolBackgroundColorPicker
            values={color}
            onChange={onColorChange}
          />
        ) : (
          <ImagePicker
            image={image}
            onImageChange={onImageChange}
            isImageBlurred={isImageBlurred}
            setIsImageBlurred={setIsImageBlurred}
          />
        )}
      </div>
    </div>
  );
};
